import {
  createContext,
  useMemo,
  useState,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";

import {
  getBaseURL,
  getCurrentUser,
  getJobNumbers,
  getScopes,
} from "../../services/api/core";

import {
  extractAppNameFromPath,
  extractDomainFromPath,
} from "../../utils/common/url";

/**
 * All context which is useful across all apps is stored here. This is limited to basic
 * data which is not likely to be changed or updated. Such as lists of current model
 * data from the backend, user data, and basic constants like base URL.
 *
 * @context CoreContext
 *
 * @typedef {Object} CoreContextType
 * @property {Object} appName - The current app name extracted from the URL (stored as ref).
 * @property {Object} appDomain - The current app domain extracted from the URL (stored as ref).
 * @property {string|null} baseURL - The base URL of the application.
 * @property {boolean} debug - Whether the backend Django DEBUG setting is enabled.
 * @property {Object|null} user - The current user object.
 * @property {Object} impersonation - Current impersonation metadata.
 * @property {boolean} userLoading - Whether the user data is currently loading.
 * @property {Function} refreshUser - Function to refresh the current user data.
 * @property {Object|null} userError - Any error that occurred while fetching user data.
 * @property {Array} jobsList - List of active job numbers only in the DB.
 * @property {Array} allJobsList - List of all job numbers (including inactive) in the DB.
 * @property {Array} scopesList - List of all active scopes of work in the DB.
 */
export const CoreContext = createContext(null);

/**
 * Read CoreContext. Throws if used outside the authenticated Core provider.
 *
 * @returns {import("react").ContextType<typeof CoreContext>}
 */
export function useCoreContext() {
  const context = useContext(CoreContext);
  if (!context) {
    throw new Error("useCoreContext must be used within a CoreProvider");
  }
  return context;
}

/**
 * Build the CoreContext value: current user, jobs, scopes, base URL, and impersonation.
 *
 * @returns {Object} Value passed to `CoreContext.Provider`.
 */
export function useCoreCtx() {
  // App name state - extracted from URL path
  const appName = useRef(null);
  const appDomain = useRef(null);

  // Extract app name from URL path on initial load only. This will normally be set by
  // the app router, but this is a fallback (usually on page refresh).
  useEffect(() => {
    if (appName.current === null) {
      const extractedAppName = extractAppNameFromPath(window.location.pathname);
      if (extractedAppName) {
        appName.current = extractedAppName;
      }
    }
    if (appDomain.current === null) {
      const extractedDomain = extractDomainFromPath(window.location.pathname);
      if (extractedDomain) {
        appDomain.current = extractedDomain;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - runs only once on mount as fallback

  // Base URL data
  const [baseURL, setBaseURL] = useState(null); // useState to trigger re-renders
  const [debug, setDebug] = useState(false);
  // User data
  const [user, setUser] = useState(null); // Store user data
  const [impersonation, setImpersonation] = useState({ active: false });
  const [userLoading, setUserLoading] = useState(true); // Track loading state
  const [userError, setUserError] = useState(null); // Track any errors
  // Core objects data
  const [jobsList, setJobsList] = useState([]); // Store active job numbers
  const [allJobsList, setAllJobsList] = useState([]); // Store all job numbers (including inactive)
  const [scopesList, setScopesList] = useState([]); // Store scopes of work

  useMemo(() => {
    // Fetch active jobs (default for most use cases)
    getJobNumbers(true)
      .then(setJobsList)
      .catch((e) => console.error(`could not get active jobs `, e));

    // Fetch all jobs (for admin/management views)
    getJobNumbers(false)
      .then(setAllJobsList)
      .catch((e) => console.error(`could not get all jobs `, e));
  }, []);

  useMemo(() => {
    getScopes()
      .then(setScopesList)
      .catch((e) => console.error(`could not get scopes `, e));
  }, []);

  useMemo(() => {
    /**
     * Loads backend base URL and DEBUG flag.
     *
     * @returns {Promise<void>}
     */
    async function fetchBaseURL() {
      const response = await getBaseURL();
      setBaseURL(response.baseURL);
      setDebug(Boolean(response.debug));
    }
    fetchBaseURL();
  }, []); // Run only once on page load as this will rarely change (mostly on deploy)

  // Fetch the current user data
  useMemo(() => {
    /**
     * Loads the session user and impersonation metadata.
     *
     * @returns {Promise<void>}
     */
    async function fetchCurrentUser() {
      try {
        setUserLoading(true);
        const response = await getCurrentUser();
        setUser(response.user);
        setImpersonation(response.impersonation || { active: false });
        setUserError(null);
      } catch (error) {
        console.error("Error fetching current user:", error);
        setUserError(error);
      } finally {
        setUserLoading(false);
      }
    }
    fetchCurrentUser();
  }, []); // Run only once on page load

  // Function to refresh user data if needed
  const refreshUser = useCallback(async () => {
    try {
      setUserLoading(true);
      const response = await getCurrentUser({ force: true });
      setUser(response.user);
      setImpersonation(response.impersonation || { active: false });
      setUserError(null);
      return response.user;
    } catch (error) {
      console.error("Error refreshing user data:", error);
      setUserError(error);
      throw error;
    } finally {
      setUserLoading(false);
    }
  }, []);

  const contextValue = useMemo(
    // Memo so if another component calls it doesn't re-run context unless changed
    () => ({
      appName,
      appDomain,
      baseURL,
      debug,
      user,
      impersonation,
      userLoading,
      userError,
      refreshUser,
      jobsList,
      allJobsList,
      scopesList,
    }),
    [
      appName,
      appDomain,
      baseURL,
      debug,
      user,
      impersonation,
      userLoading,
      userError,
      refreshUser,
      jobsList,
      allJobsList,
      scopesList,
    ]
  );

  return {
    ...contextValue,
  };
}

export default useCoreCtx; // Export the hook as default for backward compatibility
